import type { Study, Teach } from '@pocketprep/types'
import { examModule } from '@/store/exam/module'
import { instructorModule } from '@/store/instructor/module'
import { groupModule } from '@/store/group/module'
import { organizationModule } from '@/store/organization/module'
import { studentModule } from '@/store/student/module'
import { bundleModule } from '@/store/bundle/module'
import { compareSemanticVersionsDescending, isPromise } from '@/utils'
import { computed } from 'vue'

const getYourOrgExamIds = () => {
    const instructor = instructorModule.getters.getCurrentOrgInstructor()
    return groupModule.getters.searchCurrentOrgGroups({
        type: 'system',
        instructorId: instructor?.objectId,
    }).filter(g => g.orgExam).map(g => g.orgExam.objectId)
}

const getExamMetadata = () => {
    if (examModule.state.examMetadata.value && !(isPromise(examModule.state.examMetadata.value))) {
        return examModule.state.examMetadata.value
    } else {
        return {}
    }
}

const mostRecentExamMetadataByGuid = computed(() => {
    const examMetadata = getExamMetadata()
    return Object.values(examMetadata).reduce((acc, em) => {
        const examGuid = em.examGuid
        if (!(examGuid in acc)) {
            acc[examGuid] = em
            return acc
        }
        const previousExamVersion = acc[examGuid]?.version
        const examVersion = em.version
        if (previousExamVersion && previousExamVersion.localeCompare(examVersion, undefined, { numeric: true }) < 0) {
            acc[examGuid] = em
        }

        return acc
    }, {} as { [examGuid: string]: Study.Class.ExamMetadataJSON })
})

const getMostRecentExamMetadataByGuid = () => {
    return mostRecentExamMetadataByGuid.value
}

const getUniqueExamMetadata = (options?: { excludeDiscontinued?: boolean }) => {
    const guidToMetadata = getMostRecentExamMetadataByGuid()
    // Have a library of guid to EM.. convert to metadataId to examMetadata
    return Object.values(guidToMetadata).reduce((acc, em) => {
        const isDiscontinued = getIsExamMetadataRemoved(em)
        if (options?.excludeDiscontinued && isDiscontinued) {
            return acc
        }
        acc[em.objectId] = em
        return acc
    }, {} as { [objectId: string ]: Study.Class.ExamMetadataJSON })
}

const getCurrentOrgExams = () => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const currentOrganizationExams = currentOrganization
        && examModule.state.currentOrgExams[currentOrganization.objectId]?.value

    if (currentOrganizationExams && !(isPromise(currentOrganizationExams))) {
        return currentOrganizationExams
    } else {
        return {}
    }
}

const currentOrgExamsList = computed(() => {
    return Object.values(getCurrentOrgExams())
})

const getOneOrgExamPerExamGuid = () => {
    const uniqueExamMetadata = getUniqueExamMetadata()
    return currentOrgExamsList.value.filter(orgExam => !!uniqueExamMetadata[orgExam.examMetadata.objectId])
}

const getAreAllOrgExamsFree = () => {
    const examMetadata = getExamMetadata()
    const areAllOrgExamsFree = currentOrgExamsList.value.every(orgExam =>
        examMetadata[orgExam.examMetadata.objectId]?.isFree
    )
    return areAllOrgExamsFree
}

const getCurrentStudentExams = () => {
    const currentStudentId = studentModule.state.currentStudentId
    const currentStudentExams = currentStudentId && examModule.state.currentStudentExams[currentStudentId]?.value
    if (currentStudentExams && !(isPromise(currentStudentExams))) {
        return currentStudentExams
    } else {
        return []
    }
}

const getCurrentStudentCurrentExam = () => {
    const currentStudentCurrentExamId = examModule.state.currentStudentCurrentExamId
    const currentStudentExams = getCurrentStudentExams()
    if (currentStudentCurrentExamId) {
        return currentStudentExams.find(exam => exam.objectId === currentStudentCurrentExamId) || null
    } else {
        return null
    }
}

const getCurrentGroupExam = () => {
    const currentGroup = groupModule.getters.getCurrentGroup()
    const currentGroupExamId = currentGroup?.orgExam.objectId
    const currentOrgExams = getCurrentOrgExams()
    const currentGroupExam = currentGroupExamId && currentOrgExams[currentGroupExamId]

    return currentGroupExam || null
}

const getCurrentExamMetadata = () => {
    const currentGroupExam = getCurrentGroupExam()
    const examMetadata = getExamMetadata()

    const examMetadataId = currentGroupExam?.examMetadata?.objectId
    if (examMetadataId && (examMetadataId in examMetadata)) {
        return examMetadata[examMetadataId]
    }

    return null
}

const orgExamIdGuidLib = computed(() => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()

    const examMetadata = examModule.state.examMetadata.value
    const currentOrgExams = currentOrganization
        && examModule.state.currentOrgExams[currentOrganization.objectId]?.value

    if (!examMetadata
        || isPromise(examMetadata)
        || !currentOrgExams
        || isPromise(currentOrgExams)
    ) {
        return {}
    }

    return Object.values(currentOrgExams).reduce((acc, orgExam) => {
        const examGuid = orgExam && examMetadata[orgExam.examMetadata.objectId]?.examGuid

        if (!orgExam || !examGuid) {
            return acc
        }

        return {
            ...acc,
            [orgExam.objectId]: examGuid,
        }
    }, {} as { [key: string]: string })
})

const getOrgExamIdGuidLib = () => {
    return orgExamIdGuidLib.value
}

const guidOrgExamLib = computed(() => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()

    const examMetadata = examModule.state.examMetadata.value
    const currentOrgExams = currentOrganization && examModule.state.currentOrgExams[currentOrganization.objectId]?.value

    if (!examMetadata
        || isPromise(examMetadata)
        || !currentOrgExams
        || isPromise(currentOrgExams)
    ) {
        return {}
    }

    return Object.values(currentOrgExams).reduce((acc, orgExam) => {
        const examGuid = orgExam && examMetadata[orgExam.examMetadata.objectId]?.examGuid

        if (!orgExam || !examGuid) {
            return acc
        }

        if (acc[examGuid]) {
            acc[examGuid].push(orgExam)
        } else {
            acc[examGuid] = [ orgExam ]
        }

        return acc
    }, {} as { [key: string]: Teach.Class.OrgExamJSON[] })
})

const getGuidOrgExamLib = () => {
    return guidOrgExamLib.value
}

const guidExamMetadataLib = computed(() => {
    const examMetadata = examModule.state.examMetadata.value

    if (!examMetadata || isPromise(examMetadata)) {
        return {}
    }

    return Object.values(examMetadata).reduce((acc, em) => {
        if (!em) {
            return acc
        }

        if (!acc[em.examGuid]) {
            acc[em.examGuid] = []
        }

        // Could be multiple values for a guid (multiple versions)
        acc[em.examGuid]?.push(em)

        return acc
    }, {} as { [key: string]: Study.Class.ExamMetadataJSON[] })
})

const getGuidExamMetadataLib = () => {
    return guidExamMetadataLib.value
}

const getSortedExamMetadataForGuid = (examGuid: string) => {
    const examMetadataForGuid = examModule.getters.getGuidExamMetadataLib()[examGuid] || []

    const sortedExamMetadata = examMetadataForGuid.sort((a, b) => {
        return compareSemanticVersionsDescending(a.version, b.version)
    })

    // Sorted descending, so grab the first one - will return [] if none
    return sortedExamMetadata
}

const getCurrentExamQuestions = () => {
    const questions = examModule.state.currentExamQuestions.value
    if (isPromise(questions)) {
        return []
    } else {
        return questions
    }
}

// Retrieve questions that are not mock questions
const getCurrentExamQuestionsNoMock = () => {
    const questions = examModule.state.currentExamQuestions.value
    if (isPromise(questions)) {
        return []
    } else {
        return questions.filter(q => !q.isMockQuestion)
    }
}

const currentExamQuestionsBySerial = computed(() => {
    const currentExamQuestions = examModule.getters.getCurrentExamQuestions()
    return currentExamQuestions.reduce<{
        [serial: string]: Study.Class.QuestionJSON
    }>((acc, question) => {
        acc[question.serial] = question
        return acc
    }, {})
})

const getCurrentExamQuestionsBySerial = () => {
    return currentExamQuestionsBySerial.value
}

const currentExamSubjects = computed(() => {
    const currentExamQuestions = examModule.getters.getCurrentExamQuestions()
    return currentExamQuestions.reduce<{
        [subjectId: string]: Study.Class.SubjectJSON
    }>((acc, q) => {
        const subject = q.subject
        if (!subject) {
            return acc
        }

        if (!(subject.objectId in acc)) {
            acc[subject.objectId] = subject as Study.Class.SubjectJSON
        }
        return acc
    }, {})
})

const getCurrentExamSubjects = () => {
    return currentExamSubjects.value
}

const getSubjectWithLevels = (examMetadataId: string, subjectId: string) => {
    const subjectsWithLevels = examModule.state.examSubjectsWithLevels[examMetadataId]?.value
    if (isPromise(subjectsWithLevels)) {
        return null
    } else {
        const subjectLevels =
            subjectsWithLevels?.find(subjectWithLevels => subjectWithLevels.subjectId === subjectId)
        return subjectLevels || null
    }
}

const getScenarioPartLabel = (question: Study.Class.QuestionJSON) => {
    if (question.questionScenario) {
        const questionScenario = question.questionScenario as Study.Class.QuestionScenarioJSON
        const numOfScenarioQuestions = questionScenario.questions.length
        const scenarioQuestionIndex =
            questionScenario.questions.findIndex(q => q.serial === question.serial)
        const scenarioPart = scenarioQuestionIndex + 1
        return `Part ${scenarioPart} of ${numOfScenarioQuestions}`
    }
    return ''
}

const getOrgExamForExamMetadataId = (examMetadataId: string) => {
    return currentOrgExamsList.value.find(orgExam => orgExam.examMetadata.objectId === examMetadataId)
}

const getExamGuidForOrgExamId = (orgExamId: string) => {
    return getOrgExamIdGuidLib()[orgExamId]
}

const getExamGuidForOrgGroupId = (orgGroupId: string) => {
    const currentOrgGroups = groupModule.getters.getCurrentOrgGroups()
    const orgGroup = currentOrgGroups[orgGroupId]
    const orgExamId = orgGroup?.orgExam.objectId
    return orgExamId && getExamGuidForOrgExamId(orgExamId)
}

const getOrgExamForOrgGroupId = (orgGroupId: string) => {
    const currentOrgExams = getCurrentOrgExams()
    const currentOrgGroups = groupModule.getters.getCurrentOrgGroups()
    const orgGroup = currentOrgGroups[orgGroupId]
    return orgGroup && currentOrgExams[orgGroup.orgExam.objectId]
}

const getExamMetadataForOrgGroupId = (orgGroupId: string) => {
    const examMetadata = getExamMetadata()
    const orgExamForOrgGroupId = getOrgExamForOrgGroupId(orgGroupId)
    return orgExamForOrgGroupId && examMetadata[orgExamForOrgGroupId.examMetadata.objectId]
}

const getCurrentExamMetadataIdByExamGuid = () => {
    return examModule.state.currentExamMetadataIdByExamGuid
}

const getCurrentExamMetadataIdForExamGuid = (examGuid: string) => {
    const currentExamMetadataIdByExamGuid = getCurrentExamMetadataIdByExamGuid()
    const currentExamMetadataIdForExamGuid = currentExamMetadataIdByExamGuid[examGuid]
    const mostRecentExamMetadataIdForExamGuid = getMostRecentExamMetadataByGuid()[examGuid]
    return currentExamMetadataIdForExamGuid || mostRecentExamMetadataIdForExamGuid?.objectId
}

const getCurrentOrgExamForExamGuid = (examGuid: string) => {
    const currentExamMetadataIdForExamGuid = getCurrentExamMetadataIdForExamGuid(examGuid)
    const currentOrgExamForExamGuid = currentExamMetadataIdForExamGuid
        && getOrgExamForExamMetadataId(currentExamMetadataIdForExamGuid)
    return currentOrgExamForExamGuid
}

const getCurrentOrgExamForOrgGroupId = (orgGroupId: string) => {
    const examGuid = examModule.getters.getExamGuidForOrgGroupId(orgGroupId)
    const currentOrgExamForExamGuid = examGuid && examModule.getters.getCurrentOrgExamForExamGuid(examGuid)
    return currentOrgExamForExamGuid
}

const getCurrentExamVersions = () => {
    const currentExamGuid = getCurrentExamMetadata()?.examGuid
    return currentExamGuid ? examModule.getters.getSortedExamMetadataForGuid(currentExamGuid) : []
}

const getExamVersionsForExamMetadataId = (examMetadataId: string) => {
    const allExamMetadata = examModule.getters.getExamMetadata()
    const examGuid = allExamMetadata[examMetadataId]?.examGuid
    const availableExamMetadata = examGuid
        ? examModule.getters.getSortedExamMetadataForGuid(examGuid).filter(em => !getIsExamMetadataRemoved(em))
        : []
    return availableExamMetadata
}

const getDiscontinuedDateForExam = (examMetadata: Study.Class.ExamMetadataJSON) => {
    if (examMetadata.sunsetAt?.iso) {
        // We want 2025-01-01T00:00:00.000Z to be treated like Jan 1 in the user's timezone
        const sunsetAtIsoDateMinusZ = examMetadata.sunsetAt?.iso.slice(0, -1)
        const date = new Date(sunsetAtIsoDateMinusZ)
        date.setMonth(date.getMonth() + 1)
        return date
    }
    return null
}

const getIsExamMetadataRemoved = (examMetadata: Study.Class.ExamMetadataJSON) => {
    if (!bundleModule.getters.getExamBundle(examMetadata.objectId)) {
        return true
    }
    const discontinuedDate = getDiscontinuedDateForExam(examMetadata)
    if (discontinuedDate) {
        const isAfterDiscontinuedDate = new Date().getTime() - discontinuedDate.getTime() > 0
        return isAfterDiscontinuedDate
    }
    return false
}

const getMockExams = () => {
    const mockExams = examModule.state.mockExams.value
    if (isPromise(mockExams)) {
        return []
    } else {
        return mockExams
    }
}

export default {
    getYourOrgExamIds,
    getExamMetadata,
    getCurrentOrgExams,
    getAreAllOrgExamsFree,
    getCurrentStudentExams,
    getCurrentStudentCurrentExam,
    getCurrentGroupExam,
    getCurrentExamMetadata,
    getOrgExamIdGuidLib,
    getGuidOrgExamLib,
    getGuidExamMetadataLib,
    getCurrentExamQuestions,
    getCurrentExamQuestionsNoMock,
    getCurrentExamQuestionsBySerial,
    getCurrentExamSubjects,
    getUniqueExamMetadata,
    getSubjectWithLevels,
    getScenarioPartLabel,
    getSortedExamMetadataForGuid,
    getOrgExamForExamMetadataId,
    getOneOrgExamPerExamGuid,
    getExamGuidForOrgExamId,
    getExamGuidForOrgGroupId,
    getOrgExamForOrgGroupId,
    getExamMetadataForOrgGroupId,
    getCurrentExamMetadataIdByExamGuid,
    getCurrentExamMetadataIdForExamGuid,
    getCurrentOrgExamForExamGuid,
    getCurrentOrgExamForOrgGroupId,
    getCurrentExamVersions,
    getExamVersionsForExamMetadataId,
    getDiscontinuedDateForExam,
    getIsExamMetadataRemoved,
    getMockExams,
}
